import {
  areaList,
  areaName,
  ASSET_STATUS_LABELS,
  ASSET_STATUSES,
  ASSET_TYPES,
  assetDetailsSchema,
  assetFields,
  assetInputSchema,
  assetTypeName,
  defaultDetails,
  parseLatLng,
  subject,
  type AssetDetail,
  type AssetStatus,
  type DetailField,
} from '@ftthnms/shared';
import { Button } from '@ftthnms/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Save } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AssetGlyph } from '../map/AssetGlyph';
import { api, ApiError, send } from '../lib/api';
import { invalidateAssets } from '../lib/assets';
import { useSession } from '../lib/session';
import { Drawer } from './Drawer';

type Values = Record<string, string>;

const TYPE_HINT: Record<string, string> = {
  OLT: 'Perangkat pusat dengan port PON',
  SERVER: 'Router / server upstream',
  AP: 'Access point wireless',
  ODC: 'Kabinet pembagi dari PON',
  ODP: 'Titik distribusi ke pelanggan',
  JC: 'Titik sambungan kabel',
  POLE: 'Tiang penyangga kabel udara',
  CPE: 'ONT / HTB di rumah pelanggan',
};

const toStr = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/**
 * Create/edit form in a drawer. Fields come from the shared ASSET_FIELDS registry and are
 * validated with the same Zod schemas as the API. Technicians see technical fields read-only.
 */
export function AssetForm({
  mode,
  asset,
  draft,
  onClose,
  onSaved,
  pickOnMap,
  initialType,
}: {
  mode: 'create' | 'edit';
  /** Preselected type for a new asset (e.g. CPE from the customer list). */
  initialType?: string;
  asset?: AssetDetail;
  draft?: { lat: number; lng: number } | null;
  onClose: () => void;
  onSaved: (a: AssetDetail) => void;
  /** The map is behind the form: clicking it moves the point. */
  pickOnMap?: boolean;
}) {
  const { ability, areaOptions, area: pickedArea } = useSession();
  const qc = useQueryClient();
  const [type, setType] = useState<string | null>(asset?.typeCode ?? initialType ?? null);
  const [values, setValues] = useState<Values>(() => ({
    name: asset?.name ?? '',
    code: asset?.code ?? '',
    status: asset?.status ?? 'planned',
    model: toStr(asset?.model),
    description: toStr(asset?.description),
    address: toStr(asset?.address),
    price: toStr(asset?.price),
    area:
      asset?.areaCode ??
      (areaOptions !== 'all' && areaOptions.length === 1 ? areaOptions[0]! : (pickedArea ?? '')),
    coords: asset ? fmtCoords(asset.lat, asset.lng) : draft ? fmtCoords(draft.lat, draft.lng) : '',
  }));
  const [details, setDetails] = useState<Values>(() =>
    Object.fromEntries(Object.entries(asset?.details ?? {}).map(([k, v]) => [k, toStr(v)])),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [areaTouched, setAreaTouched] = useState(mode === 'edit');
  const [areaHint, setAreaHint] = useState<string | null>(null);

  // A new click on the map while creating moves the draft point.
  useEffect(() => {
    if (mode === 'create' && draft) setValues((v) => ({ ...v, coords: fmtCoords(draft.lat, draft.lng) }));
  }, [mode, draft]);

  const pickable = areaOptions === 'all' ? areaList().map((a) => a.code) : areaOptions;
  const areaLocked = pickable.length === 1;

  // Prefill the area from the drawn boundaries while the user has not chosen one.
  const position = parseLatLng(values.coords ?? '');
  useEffect(() => {
    if (areaTouched || !position) return;
    const t = setTimeout(() => {
      api<{ code: string | null }>(`/api/v1/areas/locate?lng=${position.lng}&lat=${position.lat}`)
        .then(({ code }) => {
          if (code && (pickable as readonly string[]).includes(code)) {
            setValues((v) => ({ ...v, area: code }));
            setAreaHint('Terisi otomatis dari batas wilayah');
          } else setAreaHint(code ? null : 'Titik di luar batas wilayah yang sudah digambar: pilih manual');
        })
        .catch(() => setAreaHint(null));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- position is derived from values.coords
  }, [values.coords, areaTouched]);

  const fields = useMemo(() => (type ? assetFields(type) : []), [type]);
  const sections = useMemo(() => {
    const m = new Map<string, DetailField[]>();
    for (const f of fields) m.set(f.section, [...(m.get(f.section) ?? []), f]);
    return [...m.entries()];
  }, [fields]);

  function chooseType(t: string) {
    setType(t);
    setDetails(Object.fromEntries(Object.entries(defaultDetails(t)).map(([k, v]) => [k, toStr(v)])));
  }

  const canFull =
    mode === 'create' || ability.can('update', subject('Asset', { areaCode: asset?.areaCode ?? null }));
  const statusLocked =
    mode === 'create' && !ability.can('update', subject('Asset', { areaCode: values.area || null }));
  const lockTechnical = !canFull;

  const set = (k: string, v: string) => {
    setValues((s) => ({ ...s, [k]: v }));
    setErrors((e) => ({ ...e, [k]: '' }));
  };
  const setDetail = (f: DetailField, v: string) => {
    setDetails((s) => {
      const next = { ...s, [f.key]: v };
      // Picking an ODP splitter in a new asset sets the customer port count to match.
      if (mode === 'create' && f.key === 'splitterRatio' && /^1:\d+$/.test(v))
        next.outputPorts = v.split(':')[1]!;
      return next;
    });
    setErrors((e) => ({ ...e, [`details.${f.key}`]: '' }));
  };

  async function submit() {
    if (!type) return;
    setFormError(null);
    const pos = parseLatLng(values.coords ?? '');
    const payload = {
      type,
      code: mode === 'create' ? values.code : values.code || asset?.code,
      name: values.name,
      status: statusLocked ? 'planned' : values.status,
      model: values.model,
      description: values.description,
      address: values.address,
      price: values.price,
      area: values.area || null,
      lat: pos?.lat,
      lng: pos?.lng,
      details,
    };
    const found: Record<string, string> = {};
    const common = assetInputSchema.safeParse(payload);
    if (!common.success) for (const i of common.error.issues) found[String(i.path[0])] ??= i.message;
    const det = assetDetailsSchema(type).safeParse(details);
    if (!det.success) for (const i of det.error.issues) found[`details.${String(i.path[0])}`] ??= i.message;
    if (!pos) found.coords = 'Isi koordinat "lat, lng", mis. -6.884912, 107.412387';
    if (found.lat || found.lng) found.coords ??= found.lat ?? found.lng!;
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setFormError('Periksa kembali isian yang ditandai.');
      return;
    }

    setSaving(true);
    try {
      const saved =
        mode === 'create'
          ? await send<AssetDetail>('POST', '/api/v1/assets', payload)
          : await send<AssetDetail>('PUT', `/api/v1/assets/${asset!.id}`, payload);
      await invalidateAssets(qc);
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError) {
        const fe = err.fieldErrors;
        if (fe.lat || fe.lng) fe.coords = fe.lat ?? fe.lng!;
        setErrors(fe);
      }
      setFormError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const title = mode === 'create' ? (type ? `${assetTypeName(type)} baru` : 'Pilih jenis aset') : asset!.name;

  return (
    <Drawer
      label={mode === 'create' ? 'Tambah aset' : 'Edit aset'}
      eyebrow={mode === 'create' ? 'Tambah aset' : `Edit ${assetTypeName(asset!.typeCode)}`}
      title={title}
      subtitle={mode === 'edit' ? asset!.code : undefined}
      icon={type ? <AssetGlyph type={type} /> : undefined}
      onClose={onClose}
      footer={
        type ? (
          <>
            {mode === 'create' && (
              <Button
                variant="ghost"
                onClick={() => setType(null)}
                disabled={saving}
                style={{ marginRight: 'auto' }}
              >
                Ganti jenis
              </Button>
            )}
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Batal
            </Button>
            <Button
              variant="primary"
              loading={saving}
              onClick={() => void submit()}
              icon={
                mode === 'create' ? <Plus className="i" aria-hidden /> : <Save className="i" aria-hidden />
              }
            >
              {mode === 'create' ? 'Tambah Aset' : 'Simpan'}
            </Button>
          </>
        ) : undefined
      }
    >
      {!type ? (
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          {ASSET_TYPES.map((t) => (
            <button
              key={t.code}
              type="button"
              className="fn-choice"
              style={{ cursor: 'pointer', justifyContent: 'flex-start', textAlign: 'left' }}
              onClick={() => chooseType(t.code)}
            >
              <AssetGlyph type={t.code} />
              <span>
                <span className="fn-choice__title">{t.name}</span>
                <span className="fn-choice__desc">{TYPE_HINT[t.code]}</span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          style={{ display: 'contents' }}
        >
          {formError && (
            <div className="fn-alert fn-alert--danger" role="alert">
              <div className="fn-alert__body">{formError}</div>
            </div>
          )}
          {lockTechnical && (
            <div className="fn-alert fn-alert--info">
              <div className="fn-alert__body">
                <span className="fn-alert__text" style={{ color: 'inherit' }}>
                  Sebagai teknisi Anda bisa mengubah data lapangan (status, alamat, pengukuran, pelanggan).
                  Data teknis dikunci.
                </span>
              </div>
            </div>
          )}

          <section className="fn-drawer__section">
            <div className="fn-panel__label" style={{ padding: 0 }}>
              Umum
            </div>
            <div className="fn-form-grid">
              <Field id="f-name" label="Nama" required error={errors.name} wide>
                <input
                  id="f-name"
                  className="fn-input"
                  value={values.name}
                  disabled={lockTechnical}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder={type === 'CPE' ? 'Nama pelanggan / rumah' : `${assetTypeName(type)} Jalan …`}
                  autoFocus={mode === 'create'}
                />
              </Field>
              <Field
                id="f-code"
                label="Kode aset"
                error={errors.code}
                hint={mode === 'create' ? 'Kosongkan untuk kode otomatis' : undefined}
              >
                <input
                  id="f-code"
                  className="fn-input fn-input--mono"
                  value={values.code}
                  disabled={lockTechnical}
                  onChange={(e) => set('code', e.target.value)}
                  placeholder="Otomatis"
                />
              </Field>
              <Field
                id="f-status"
                label="Status"
                error={errors.status}
                hint={statusLocked ? 'Aset baru dari teknisi berstatus Rencana' : undefined}
              >
                <select
                  id="f-status"
                  className="fn-input"
                  value={statusLocked ? 'planned' : values.status}
                  disabled={statusLocked}
                  onChange={(e) => set('status', e.target.value)}
                >
                  {ASSET_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {ASSET_STATUS_LABELS[s as AssetStatus]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                id="f-coords"
                label="Koordinat (lat, lng)"
                required
                error={errors.coords}
                hint={
                  pickOnMap && mode === 'create'
                    ? 'Klik peta untuk memindahkan titik'
                    : 'Bisa ditempel dari Google Maps'
                }
              >
                <input
                  id="f-coords"
                  className="fn-input fn-input--mono"
                  value={values.coords}
                  onChange={(e) => set('coords', e.target.value)}
                  placeholder="-6.884912, 107.412387"
                />
              </Field>
              <Field id="f-area" label="Wilayah" required error={errors.area} hint={areaHint ?? undefined}>
                <select
                  id="f-area"
                  className="fn-input"
                  value={values.area}
                  disabled={areaLocked || lockTechnical}
                  onChange={(e) => {
                    setAreaTouched(true);
                    setAreaHint(null);
                    set('area', e.target.value);
                  }}
                >
                  <option value="">Otomatis dari batas wilayah</option>
                  {pickable.map((code) => (
                    <option key={code} value={code}>
                      {areaName(code)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field id="f-address" label="Alamat" error={errors.address} wide>
                <input
                  id="f-address"
                  className="fn-input"
                  value={values.address}
                  onChange={(e) => set('address', e.target.value)}
                  placeholder="Jalan, RT/RW, desa"
                />
              </Field>
              <Field id="f-model" label="Model / tipe" error={errors.model}>
                <input
                  id="f-model"
                  className="fn-input"
                  value={values.model}
                  disabled={lockTechnical}
                  onChange={(e) => set('model', e.target.value)}
                  placeholder={type === 'OLT' ? 'ZTE C320' : type === 'POLE' ? 'Tiang beton 7 m' : ''}
                />
              </Field>
              <Field id="f-price" label="Harga (Rp)" error={errors.price}>
                <input
                  id="f-price"
                  className="fn-input fn-input--mono"
                  inputMode="numeric"
                  value={values.price}
                  disabled={lockTechnical}
                  onChange={(e) => set('price', e.target.value)}
                  placeholder="0"
                />
              </Field>
            </div>
          </section>

          {sections.map(([section, fs]) => (
            <section key={section} className="fn-drawer__section">
              <div className="fn-panel__label" style={{ padding: 0 }}>
                {section}
              </div>
              <div className="fn-form-grid">
                {fs.map((f) => (
                  <DetailInput
                    key={f.key}
                    field={f}
                    value={details[f.key] ?? ''}
                    error={errors[`details.${f.key}`]}
                    disabled={lockTechnical && f.edit === 'technical'}
                    onChange={(v) => setDetail(f, v)}
                    showPortHint={mode === 'edit'}
                  />
                ))}
              </div>
            </section>
          ))}

          <section className="fn-drawer__section">
            <Field id="f-desc" label="Deskripsi / catatan" error={errors.description} wide>
              <textarea
                id="f-desc"
                className="fn-input"
                rows={3}
                value={values.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </Field>
          </section>
          <button type="submit" hidden />
        </form>
      )}
    </Drawer>
  );
}

function DetailInput({
  field: f,
  value,
  error,
  disabled,
  onChange,
  showPortHint,
}: {
  field: DetailField;
  value: string;
  error?: string;
  disabled: boolean;
  onChange: (v: string) => void;
  showPortHint: boolean;
}) {
  const id = `d-${f.key}`;
  const hint = f.drivesPorts && showPortHint ? 'Port ditambah/dikurangi otomatis' : f.hint;
  let control: ReactNode;
  if (f.input === 'select') {
    control = (
      <select
        id={id}
        className="fn-input"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {!f.required && f.default === undefined && <option value="">—</option>}
        {f.options?.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  } else if (f.input === 'textarea') {
    control = (
      <textarea
        id={id}
        className="fn-input"
        rows={3}
        value={value}
        disabled={disabled}
        placeholder={f.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else {
    const input = (
      <input
        id={id}
        className={`fn-input${f.input === 'ip' || f.input === 'number' ? ' fn-input--mono' : ''}`}
        value={value}
        disabled={disabled}
        placeholder={f.placeholder}
        inputMode={f.input === 'number' ? 'decimal' : f.input === 'tel' ? 'tel' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );
    control = f.unit ? (
      <div className="fn-input-group">
        {input}
        <span className="fn-input-group__suffix">{f.unit}</span>
      </div>
    ) : (
      input
    );
  }
  return (
    <Field id={id} label={f.label} required={f.required} error={error} hint={hint} wide={f.wide}>
      {control}
    </Field>
  );
}

function Field({
  id,
  label,
  required,
  error,
  hint,
  wide,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`fn-field${wide ? ' span-2' : ''}`}>
      <label className="fn-label" htmlFor={id}>
        {label} {required && <span className="req">*</span>}
      </label>
      {children}
      {error ? (
        <span className="fn-hint fn-hint--error" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="fn-hint">{hint}</span>
      )}
    </div>
  );
}

function fmtCoords(lat: number, lng: number) {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}
